import React, { useState } from 'react';
import { HeroSection } from '../../components/HeroSection';
import { motion } from 'framer-motion';
import { Heart, Users, Shield, Megaphone, BookOpen, Sparkles, Check } from 'lucide-react';
export function Commitments() {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const commitments = [{
    icon: <Heart className="h-10 w-10" data-id="element-848" />,
    title: 'Centering Fat People',
    shortDesc: 'Fat voices lead. Thin people are accomplices, not experts.',
    fullDesc: 'We center fat people—especially fat Black, Indigenous, disabled, trans, and poor people—in all our work. Thin people are welcome as accomplices and learners, but this is not a space for them to center their own experiences or speak over fat voices.',
    practices: ['Fat people on our team and board', 'Compensation for fat contributors', "No thin people as 'body image' experts", 'Accountability to fat community']
  }, {
    icon: <Users className="h-10 w-10" data-id="element-849" />,
    title: 'Intersectionality',
    shortDesc: 'Fat liberation is bound to all liberation struggles.',
    fullDesc: 'We reject single-issue politics. Fat liberation cannot be separated from anti-racism, disability justice, trans liberation, and economic justice. We name how anti-fatness is rooted in white supremacy, ableism, and capitalism.',
    practices: ['Centering most marginalized fat people', 'Naming systems of oppression', 'Coalition building across movements', 'Refusing to separate oppressions']
  }, {
    icon: <Shield className="h-10 w-10" data-id="element-850" />,
    title: 'Harm Reduction',
    shortDesc: 'We meet people where they are, without judgment.',
    fullDesc: "We practice harm reduction, not purity politics. People can be in different places in their unlearning. We don't require ideological perfection, but we do require accountability and willingness to grow.",
    practices: ['No shaming for diet talk recovery', 'Recognizing internalized fatphobia', 'Supporting messy unlearning', 'Clear boundaries without punishment']
  }, {
    icon: <Megaphone className="h-10 w-10" data-id="element-851" />,
    title: 'Accessibility',
    shortDesc: 'Our spaces are designed for disabled fat people.',
    fullDesc: 'Accessibility is not an afterthought. We design for disabled fat people from the start. This includes physical access, communication access, and economic access through sliding scale and scholarships.',
    practices: ['Captions and transcripts', 'Screen reader compatibility', 'Plain language', 'Sliding scale pricing']
  }, {
    icon: <BookOpen className="h-10 w-10" data-id="element-852" />,
    title: 'Intellectual Rigor',
    shortDesc: 'We take ideas seriously and cite our sources.',
    fullDesc: "We are committed to intellectual depth and rigor. We cite our sources, name our lineage, and engage with theory. We don't simplify complex ideas for palatability, but we do make them accessible.",
    practices: ['Citing fat scholars and activists', 'Engaging with academic work', 'Transparent methodology', 'Crediting intellectual labor']
  }, {
    icon: <Sparkles className="h-10 w-10" data-id="element-853" />,
    title: 'Joy & Pleasure',
    shortDesc: 'Liberation work includes rest, play, and pleasure.',
    fullDesc: 'We refuse the narrative that justice work must be joyless. Fat people deserve pleasure, rest, and celebration. Our spaces make room for laughter, creativity, and delight alongside the hard work of resistance.',
    practices: ['Celebrating fat joy', 'Rest as resistance', 'Creative expression', 'Community care rituals']
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-854">
      <HeroSection title="Our Commitments & Principles" subtitle="The values that guide everything we do." tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-855" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center" data-id="element-856">
        <p className="text-xl text-gray-700 leading-relaxed" data-id="element-857">
          These aren't aspirational values we hope to embody someday. These are{' '}
          <strong className="text-brand-magenta" data-id="element-858">active commitments</strong> we
          practice daily, fail at sometimes, and hold ourselves accountable to.
        </p>
      </section>

      {/* Commitments Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-859">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-860">
          {commitments.map((commitment, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: index * 0.1
        }} viewport={{
          once: true
        }} className={`border-2 border-brand-black bg-white p-6 cursor-pointer transition-all ${expandedCard === index ? 'md:col-span-2 lg:col-span-3 shadow-2xl' : 'hover:shadow-lg'}`} onClick={() => setExpandedCard(expandedCard === index ? null : index)} data-id="element-861">
              <div className="flex items-start justify-between mb-4" data-id="element-862">
                <div className="text-brand-magenta" data-id="element-863">{commitment.icon}</div>
                <div className={`w-6 h-6 border-2 border-brand-magenta rounded-full flex items-center justify-center transition-colors ${expandedCard === index ? 'bg-brand-magenta' : ''}`} data-id="element-864">
                  {expandedCard === index && <Check className="h-4 w-4 text-white" data-id="element-865" />}
                </div>
              </div>

              <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-866">
                {commitment.title}
              </h3>

              <p className="text-gray-700 text-sm mb-4" data-id="element-867">
                {commitment.shortDesc}
              </p>

              {expandedCard === index && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="border-t-2 border-gray-100 pt-4 mt-4" data-id="element-868">
                  <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-869">
                    {commitment.fullDesc}
                  </p>

                  <div data-id="element-870">
                    <h4 className="text-sm font-bold uppercase text-brand-magenta mb-3" data-id="element-871">
                      How We Practice This:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-872">
                      {commitment.practices.map((practice, i) => <div key={i} className="flex items-start bg-brand-magenta/5 p-3 rounded-sm" data-id="element-873">
                          <span className="text-brand-magenta mr-2 mt-0.5" data-id="element-874">
                            ✓
                          </span>
                          <span className="text-sm text-gray-700" data-id="element-875">
                            {practice}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </motion.div>}
            </motion.div>)}
        </div>

        <p className="text-center text-sm text-gray-500 mt-8" data-id="element-876">
          Click any card to expand and see how we practice these commitments
        </p>
      </section>

      {/* Accountability */}
      <section className="py-20 bg-brand-magenta text-white" data-id="element-877">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-878">
          <h2 className="text-3xl font-black uppercase mb-8" data-id="element-879">
            Accountability Matters
          </h2>
          <p className="text-xl mb-8 opacity-90" data-id="element-880">
            We don't always get it right. When we cause harm, we commit to:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left" data-id="element-881">
            <div className="bg-white/10 p-6 rounded-sm backdrop-blur-sm" data-id="element-882">
              <h3 className="font-bold text-lg mb-2" data-id="element-883">1. Listen</h3>
              <p className="text-sm opacity-90" data-id="element-884">
                Center the voices of those harmed without defensiveness
              </p>
            </div>
            <div className="bg-white/10 p-6 rounded-sm backdrop-blur-sm" data-id="element-885">
              <h3 className="font-bold text-lg mb-2" data-id="element-886">2. Acknowledge</h3>
              <p className="text-sm opacity-90" data-id="element-887">
                Name the harm publicly and take responsibility
              </p>
            </div>
            <div className="bg-white/10 p-6 rounded-sm backdrop-blur-sm" data-id="element-888">
              <h3 className="font-bold text-lg mb-2" data-id="element-889">3. Repair</h3>
              <p className="text-sm opacity-90" data-id="element-890">
                Make material changes to prevent future harm
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}